import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGroup } from '../../contexts/GroupContext';
import { useToast } from '../common';
import api from '../../services/api';

// A GROUP YOU ARE NOT IN. Every /group/<id>/… page passes through here. The
// roster can be wrong both ways — you were removed since it was read, or the id
// is in no roster at all — so the server decides: a 403 (not a member) or 404
// (no such group) takes you to your groups, saying why, instead of a page drawn
// as an empty group you could act on. A group the server let you into is not
// asked again while the roster still lists it.
const verified = new Set();
export const resetGroupGuard = () => verified.clear();

export default function GroupGuard({ children }) {
    const { groupId } = useParams();
    const { groups, refreshGroups } = useGroup();
    const toast = useToast();
    const navigate = useNavigate();
    // read when the answer comes, so a roster refresh does not ask again
    const roster = useRef(groups);
    roster.current = groups;

    useEffect(() => {
        if (!groupId) return undefined;
        if (verified.has(groupId) && roster.current.some((g) => String(g.id) === groupId)) return undefined;
        let live = true;
        api.getGroup(groupId).then(() => { verified.add(groupId); }).catch((e) => {
            if (!live || (e?.status !== 403 && e?.status !== 404)) return;
            verified.delete(groupId);
            const name = roster.current.find((g) => String(g.id) === groupId)?.name;
            toast.info(name ? `You’re no longer in ${name}` : 'That group doesn’t exist');
            refreshGroups?.({ quiet: true });
            navigate('/groups', { replace: true });
        });
        return () => { live = false; };
    }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

    return children;
}
